import type { chromeTitles } from './i18n/messages/en/chromeTitles.js';
import type { GENERIC_VIEW_SEED_IDS } from './view-engine/generic-view-seed-headings.js';

/**
 * T-0838 (`docs/concept-internationalisation.md` §5.12): what a title of the chrome (the browser
 * tab's, the bar's, the back row's) names. A view by its key in `chromeTitles`, which the screen
 * that draws it translates; or a record by its own name, which is never translated (IN-31).
 *
 * ⛔ Platform-agnostic: it imports no translator, it is handed one where the label is drawn.
 */
export type ChromeTitleKeyType = `chromeTitles.${keyof typeof chromeTitles}`;

export type ChromeLabelType =
  | { readonly key: ChromeTitleKeyType; readonly text?: never }
  | { readonly text: string; readonly key?: never };

/**
 * Whatever translates a chrome title: a screen's `useT()`, or the shared `createTranslator`. Narrowed
 * to these keys so both fit.
 */
export type ChromeTranslateFn = (key: ChromeTitleKeyType) => string;

/** A view's name, translated where it is drawn. */
export function viewLabel(name: keyof typeof chromeTitles): ChromeLabelType {
  return { key: `chromeTitles.${name}` };
}

type GenericViewSeedIdType = (typeof GENERIC_VIEW_SEED_IDS)[keyof typeof GENERIC_VIEW_SEED_IDS];

/**
 * T-0846 (IN-34, IN-36): a built-in view's name is the product's, never the person's: the server
 * refuses to rename one (I-1335) and a copy is a saved view of its own. So it is a key, by seed id.
 */
const BUILT_IN_VIEW_TITLES: Readonly<Record<GenericViewSeedIdType, keyof typeof chromeTitles>> = {
  'work-items': 'viewWorkItems',
  roadmap: 'viewRoadmap',
  activity: 'viewActivity',
  'activity-list': 'viewActivityList',
  'agent-usage': 'viewTokenUsage',
};

/** The built-in view `seedId` names, by its key; null for an id no built-in has. */
export function builtInViewLabel(seedId: string): ChromeLabelType | null {
  if (!Object.prototype.hasOwnProperty.call(BUILT_IN_VIEW_TITLES, seedId)) return null;
  return viewLabel(BUILT_IN_VIEW_TITLES[seedId as GenericViewSeedIdType]);
}

/** A record's own name (an item's key, a page's or a saved view's name), drawn as it is. */
export function recordLabel(text: string): ChromeLabelType {
  return { text };
}

/** The label in the reader's language: a view's name through `t`, a record's name as it is. */
export function chromeLabelText(label: ChromeLabelType, t: ChromeTranslateFn): string {
  return label.key !== undefined ? t(label.key) : label.text;
}

/** Whether two labels name the same thing, in any language. */
export function sameChromeLabel(a: ChromeLabelType, b: ChromeLabelType): boolean {
  return a.key !== undefined ? a.key === b.key : b.key === undefined && a.text === b.text;
}
